import { computed, makeObservable, observableShallow, actionBound } from "mobx";

let _id = 0;

export interface InjectBaseModalProps {
    /**
     * 指定全局唯一key
     */
    key?: string;

    open?: boolean;
    onCancel?(): void;
}

export interface ModalInstance {
    modalId: number | string;
    onCancel(): void;
    update(updateProps: any): void;
}

type InjectComponent<T> =
    | React.FunctionComponent<T>
    | React.ForwardRefExoticComponent<T>
    | React.ClassicComponentClass<T>
    | React.ComponentClass<T>;

export class ModalStore {
    constructor() {
        makeObservable(this, {
            modalsMapping: observableShallow,
            modalPropsMapping: observableShallow,
            needToRenderChildrenIds: computed,
            updateModalProps: actionBound,
            openModal: actionBound,
        });
    }

    modalsMapping = new Map<number | string, any>();

    modalPropsMapping = new Map<number | string, any>();

    get needToRenderChildrenIds() {
        const ids: (number | string)[] = [];

        this.modalsMapping.forEach((el, key) => {
            ids.push(key);
        });

        return ids;
    }

    /**
     * 更新弹框传入内容
     */
    updateModalProps(modalId: number | string, updateProps: any) {
        const oldProps = this.modalPropsMapping.get(modalId);

        if (!oldProps) {
            this.modalsMapping.delete(modalId);
            this.modalPropsMapping.delete(modalId);

            return;
        }

        const newProps: InjectBaseModalProps = {
            ...oldProps,
            ...updateProps,
        };

        this.modalPropsMapping.set(modalId, newProps);

        if (newProps.open === false) {
            this.modalsMapping.delete(modalId);
            this.modalPropsMapping.delete(modalId);
        }
    }

    /**
     * 打开弹框
     */
    openModal<T extends InjectBaseModalProps>(Component: InjectComponent<T>, injectProps?: T): ModalInstance {
        let modalId = injectProps?.key ? injectProps?.key : _id++;

        const update = (updateProps: Partial<T>) => {
            this.updateModalProps(modalId, updateProps);
        };

        const onCancel = () => {
            try {
                injectProps?.onCancel?.();
            } catch (error) {}

            const currentProps = this.modalPropsMapping.get(modalId);

            // 避免可能重复调用onCancel 导致的后续操作中断
            if (currentProps) {
                this.modalPropsMapping.set(modalId, {
                    ...currentProps,
                    open: false,
                });
            }

            setTimeout(() => {
                this.modalsMapping.delete(modalId);
                this.modalPropsMapping.delete(modalId);
            }, 300);
        };

        const props: InjectBaseModalProps = {
            ...injectProps,
            open: true,
            onCancel,
        };

        this.modalPropsMapping.set(modalId, props);
        this.modalsMapping.set(modalId, Component);

        return {
            modalId,
            onCancel,
            update,
        };
    }
}
